import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { OnboardingFlow } from "@/components/onboarding/OnboardingFlow";
import { API_BASE_URL } from "@/config";

const API_URL = API_BASE_URL;

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Add request interceptor to inject PostHog headers
axios.interceptors.request.use(function (config) {
  try {
    // @ts-ignore
    if (window.posthog) {
      // @ts-ignore
      const distinctId = window.posthog.get_distinct_id();
      // @ts-ignore
      const sessionId = window.posthog.get_session_id();
      
      if (distinctId) {
        config.headers['X-PostHog-Distinct-Id'] = distinctId;
      }
      if (sessionId) {
        config.headers['X-PostHog-Session-Id'] = sessionId;
      }
      // Send current URL for context
      config.headers['X-PostHog-Current-Url'] = window.location.href;
    }
  } catch (e) {
    console.error("Error injecting PostHog headers", e);
  }
  return config;
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [searchQueries, setSearchQueries] = useState<any[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [days] = useState(90);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testingPrompt, setTestingPrompt] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Track the current generation request so we can cancel it
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadSites = useCallback(
    async (uid: string | null) => {
      try {
        setLoading(true);
        setAuthError(null);
        const response = await axios.get(`${API_URL}/api/sites`, {
          params: { user_id: uid || userId },
        });
        setSites(response.data.sites || []);
      } catch (error) {
        console.error("Error loading sites:", error);
        setAuthError("Failed to load sites. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    // Check if user just authenticated
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get("auth_success");
    const authErrorParam = urlParams.get("auth_error");
    const userIdParam = urlParams.get("user_id");

    if (authSuccess === "true" && userIdParam) {
      setUserId(userIdParam);
      setIsAuthenticated(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      loadSites(userIdParam);
    } else if (authErrorParam === "missing_scope") {
      setAuthError("missing_scope");
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (authErrorParam) {
      setAuthError(authErrorParam);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadSites]);

  const handleLogin = async () => {
    setAuthError(null);
    try {
      // First check if backend is healthy
      try {
        const healthCheck = await axios.get(`${API_URL}/health`);
        if (
          !healthCheck.data.config.client_id_configured ||
          !healthCheck.data.config.client_secret_configured
        ) {
          alert(
            "Backend is not configured. Please set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in your backend .env file."
          );
          return;
        }
      } catch (healthError) {
        alert(
          "Cannot connect to backend. Please make sure the backend server is running."
        );
        console.error("Health check error:", healthError);
        return;
      }

      // @ts-ignore
      const distinctId = window.posthog?.get_distinct_id();
      const currentUrl = window.location.href;
      
      const response = await axios.get(`${API_URL}/auth/login`, {
        params: {
          distinct_id: distinctId,
          current_url: currentUrl
        }
      });
      window.location.href = response.data.auth_url;
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.detail || error.message || "Unknown error";
      alert(
        `Failed to initiate login: ${errorMessage}\n\nPlease check:\n1. Backend server is running\n2. .env file is configured with Google OAuth credentials`
      );
    }
  };



  const testPrompt = async (prompt: any, overrideUserId?: string, overrideBrandName?: string, overrideSiteUrl?: string) => {
    const activeUserId = overrideUserId || userId;
    const activeBrandName = overrideBrandName || brandName;
    const activeSiteUrl = overrideSiteUrl || selectedSite;
    
    if (!activeUserId) {
      alert("Please authenticate first");
      return null;
    }

    try {
      setTestingPrompt(true);
      // We don't set selectedPrompt/showSignupModal here anymore, letting the component handle it
      // unless we want to keep the old behavior for other calls? 
      // For now, let's assume OnboardingFlow handles the UI for testing.
      
      const response = await axios.post(
        `${API_URL}/api/test-prompt`,
        {
          user_id: activeUserId,
          prompt: prompt.prompt,
          brand_name: activeBrandName || null,
          site_url: activeSiteUrl || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      setTestResult(response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error testing prompt:", error);
      alert(
        error.response?.data?.detail ||
          "Failed to test prompt. Please try again."
      );
      return null;
    } finally {
      setTestingPrompt(false);
    }
  };

  // To avoid unused variable warnings for state that will be used later or is used in the flow
  useEffect(() => {
    console.log("State updated:", { selectedSite, searchQueries, prompts, days });
  }, [selectedSite, searchQueries, prompts, days]);



  // Placeholder for handleFlowGeneration, as it's called in the new OnboardingFlow prop
  
  // Cancel any in-flight prompt generation
  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      console.log("Cancelling in-flight prompt generation request");
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
    setPrompts([]);
  }, []);

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-white">
      <OnboardingFlow
        onLogin={handleLogin}
        isAuthenticated={isAuthenticated}
        sites={sites}
        isLoadingSites={loading}
        authError={authError}
        prompts={prompts}
        isGeneratingPrompts={isGenerating}
        onGeneratePrompts={async (data) => {
          console.log("Generating prompts with data:", data);
          
          // Cancel any existing request first
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          
          // Create new AbortController for this request
          const abortController = new AbortController();
          abortControllerRef.current = abortController;
          
          setIsGenerating(true);
          try {
            const currentUserId = userId || `guest_${Date.now()}`;
            if (!userId) setUserId(currentUserId);

            let queries = [];
            
            if (data.method === "search_console") {
              // Fetch search queries first
              try {
                const queriesResponse = await axios.get(`${API_URL}/api/search-queries`, {
                  params: {
                    user_id: currentUserId,
                    site_url: data.siteUrl,
                    days: days,
                  },
                  signal: abortController.signal,
                });
                queries = queriesResponse.data.queries || [];
                setSearchQueries(queries);
              } catch (err: any) {
                if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
                  console.log("Search queries request was cancelled");
                  return;
                }
                if (err.response?.status === 403) {
                  alert(err.response?.data?.detail || "This domain has been blocked.");
                  return;
                }
                console.error("Failed to fetch queries:", err);
                alert("Failed to fetch Search Console data. Proceeding with site content analysis only.");
              }
            }

            // 1. Create and show generic prompt immediately
            const brand = data.brandName || "this brand";
            const genericPrompt = {
              prompt: `What is ${brand} and what do they offer?`,
              type: "awareness",
              isLoading: true
            };
            
            // Set initial state with generic prompt
            setPrompts([genericPrompt]);
            setSelectedSite(data.siteUrl);
            setBrandName(data.brandName || "");
            
            // Trigger background generation for generic prompt immediately
            // We use a separate async function to not block the main flow
            const generateGenericResponse = async () => {
              try {
                console.log("Triggering background generation for generic prompt:", genericPrompt.prompt);
                const result = await testPrompt({
                  prompt: genericPrompt.prompt,
                  type: genericPrompt.type
                }, currentUserId, data.brandName, data.siteUrl);
                
                if (result && result.response) {
                  setPrompts(currentPrompts => {
                    // Update the generic prompt (assuming it's the first one or finding it by prompt text)
                    return currentPrompts.map(p => {
                      if (p.prompt === genericPrompt.prompt) {
                        return {
                          ...p,
                          response: result.response,
                          citations: result.citations,
                          brand_mentioned: result.brand_mentioned,
                          isLoading: false
                        };
                      }
                      return p;
                    });
                  });
                } else {
                   setPrompts(currentPrompts => {
                    return currentPrompts.map(p => {
                      if (p.prompt === genericPrompt.prompt) {
                        return { ...p, isLoading: false };
                      }
                      return p;
                    });
                  });
                }
              } catch (e) {
                console.error("Generic prompt generation failed:", e);
                setPrompts(currentPrompts => {
                    return currentPrompts.map(p => {
                      if (p.prompt === genericPrompt.prompt) {
                        return { ...p, isLoading: false };
                      }
                      return p;
                    });
                  });
              }
            };
            
            // Start generic prompt generation
            generateGenericResponse();

            // 2. Generate specific prompts in parallel
            const requestData = {
              user_id: currentUserId,
              queries: queries,
              brand_name: data.brandName || null,
              site_url: data.siteUrl,
            };

            const response = await axios.post(
              `${API_URL}/api/generate-prompts`,
              requestData,
              {
                headers: {
                  "Content-Type": "application/json",
                },
                signal: abortController.signal,
              }
            );
            
            // Only update state if this request wasn't cancelled
            if (!abortController.signal.aborted) {
              const generatedPrompts = response.data.prompts || [];
              
              // Append new prompts to existing ones (keeping the generic one)
              setPrompts(currentPrompts => {
                // Filter out duplicates if any (though unlikely)
                const newPrompts = generatedPrompts.filter((np: any) => 
                  !currentPrompts.some(cp => cp.prompt === np.prompt)
                );
                return [...currentPrompts, ...newPrompts];
              });
              
              // We don't need to trigger testPrompt for the first *generated* prompt anymore
              // because we already tested the generic one.
              // Unless we want to? User said "don't wait... before we run the first test prompt".
              // This implies the generic one IS the first test prompt.
            }
            
          } catch (error: any) {
            // Don't show error if request was cancelled
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
              console.log("Prompt generation request was cancelled");
              return;
            }
            console.error("Error generating prompts:", error);
            const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
            alert(`Failed to generate prompts: ${errorMessage}`);
          } finally {
            // Only reset isGenerating if this is still the current request
            if (abortControllerRef.current === abortController) {
              setIsGenerating(false);
              abortControllerRef.current = null;
            }
          }
        }}
        onCancelGeneration={cancelGeneration}
        onTestPrompt={testPrompt}
        isTestingPrompt={testingPrompt}
      />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 text-white">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/30 border-t-white"></div>
          <p className="mt-4 text-sm">Loading...</p>
        </div>
      )}

      {/* Test Prompt Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-slate-950">
                Test Prompt with ChatGPT
              </h3>
              <button
                onClick={() => {
                  setShowSignupModal(false);
                  setSelectedPrompt(null);
                  setTestResult(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {selectedPrompt && (
              <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-500 mb-2">Prompt:</p>
                <p className="text-sm font-medium text-slate-950">
                  "{selectedPrompt.prompt}"
                </p>
              </div>
            )}

            {testingPrompt && (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500/30 border-t-blue-500"></div>
                  <p className="text-sm text-gray-600">
                    Testing prompt with ChatGPT...
                  </p>
                </div>
              </div>
            )}

            {testResult && !testingPrompt && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      fill="currentColor"
                      fillRule="evenodd"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      height="20"
                      width="20"
                      className="text-blue-600"
                    >
                      <path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z"></path>
                    </svg>
                    <span className="font-semibold text-blue-600">
                      ChatGPT Response
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {testResult.response}
                  </p>
                </div>

                {testResult.brand_name && (
                  <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-700">
                        Brand Presence:
                      </span>
                      {testResult.brand_mentioned ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Mentioned
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-sm">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Not Mentioned
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Brand: {testResult.brand_name}
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowSignupModal(false);
                      setSelectedPrompt(null);
                      setTestResult(null);
                    }}
                    className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-black text-white hover:bg-gray-800 h-10 px-4 py-2"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {!testResult && !testingPrompt && (
              <div className="flex gap-3">
                <a
                  href="https://accounts.scrunchai.com/sign-up/?from=%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-black text-white hover:bg-gray-800 h-10 px-4 py-2"
                >
                  Sign Up for Free
                </a>
                <button
                  onClick={() => {
                    setShowSignupModal(false);
                    setSelectedPrompt(null);
                    setTestResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
